import { queryOptions, useQuery } from '@tanstack/react-query';
import { DeliveryService, DeliveryDTO, DeliveryFilterDTO } from '../generated';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { SortDTO } from '../common';

export const deliveryKeys = {
  base: ['delivery'] as const,
  list: ({ jobsiteId, filter, pageSize, pageNumber, sort }: DeliveryListOptionsProps) =>
    [...deliveryKeys.base, jobsiteId, filter, pageSize, pageNumber, sort] as const,
  read: ({ jobsiteId, deliveryId }: DeliveryOptionsProps) => [...deliveryKeys.base, jobsiteId, deliveryId] as const,
};

export interface DeliveryOptionsProps {
  jobsiteId: string | undefined;
  deliveryId: string | undefined;
}

export interface DeliveryListOptionsProps {
  jobsiteId: string | undefined;
  filter: DeliveryFilterDTO | undefined;
  pageSize: number;
  pageNumber: number;
  sort: SortDTO<DeliveryDTO>;
}

export function deliveryOptions({ jobsiteId, deliveryId }: DeliveryOptionsProps) {
  return queryOptions<DeliveryDTO, ApiError>({
    queryKey: deliveryKeys.read({ jobsiteId, deliveryId }),
    queryFn: async () => {
      if (!jobsiteId || !deliveryId) {
        throw new Error('jobsiteId and deliveryId are required');
      }

      const response = await DeliveryService.deliveryControllerGetDelivery({
        path: {
          jobsiteId,
          deliveryId,
        },
      });

      if (response.data !== undefined) {
        return response.data;
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!deliveryId,
  });
}

export function deliveryListOptions({ jobsiteId, filter, pageSize, pageNumber, sort }: DeliveryListOptionsProps) {
  return queryOptions({
    queryKey: deliveryKeys.list({ jobsiteId, filter, pageSize, pageNumber, sort }),
    queryFn: async () => {
      const response = await DeliveryService.deliveryControllerGetDeliveries({
        path: { jobsiteId: jobsiteId || '' },
        query: {
          filter: [filter || {}],
          limit: pageSize,
          offset: pageNumber * pageSize,
          ...sort,
        },
      });

      if (response.data !== undefined) {
        return response.data;
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId,
  });
}

export function useDelivery(props: DeliveryOptionsProps) {
  return useQuery(deliveryOptions(props));
}

export function useDeliveryList(props: DeliveryListOptionsProps) {
  return useQuery(deliveryListOptions(props));
}
